import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/client';
import {
  TrendingUp,
  Brain,
  Flame,
  Target,
  Award,
  Calendar,
  BarChart3
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [streakInfo, setStreakInfo] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const dictionaryType = location.state?.dictionaryType || localStorage.getItem('dictionaryType') || 'hsk';

  useEffect(() => {
    fetchAllStats();
  }, [dictionaryType]);

  const fetchAllStats = async () => {
    try {
      const [statsRes, dailyRes, streakRes, heatmapRes] = await Promise.all([
        api.get('/api/stats/learning', { params: { dictionary_type: dictionaryType } }),
        api.get('/api/stats/daily', { params: { dictionary_type: dictionaryType } }),
        api.get('/api/stats/streak', { params: { dictionary_type: dictionaryType } }),
        api.get('/api/stats/heatmap', { params: { dictionary_type: dictionaryType } })
      ]);
      setStats(statsRes.data);
      setDailyStats(dailyRes.data);
      setStreakInfo(streakRes.data);
      setHeatmapData(heatmapRes.data.heatmap || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Data for charts
  const cardDistribution = [
    { name: 'Learning', value: stats?.learning_cards || 0, color: '#f59e0b' },
    { name: 'Review', value: stats?.review_cards || 0, color: '#3b82f6' },
    { name: 'Mastered', value: stats?.mastered_cards || 0, color: '#10b981' },
  ];

  const orderedHeatmap = [...heatmapData].sort((a, b) => a.date.localeCompare(b.date));
  const recentActivity = orderedHeatmap.slice(-30).map((day, index) => ({
    date: new Date(`${day.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    axisDate: index % 5 === 0 ? new Date(`${day.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    reviews: day.count
  }));
  const heatmapStartPadding = orderedHeatmap.length > 0
    ? new Date(`${orderedHeatmap[0].date}T00:00:00`).getDay()
    : 0;
  const heatmapCells = [
    ...Array.from({ length: heatmapStartPadding }, () => null),
    ...orderedHeatmap
  ];
  const totalDistribution = cardDistribution.reduce((total, item) => total + item.value, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Learning Statistics</h1>
        <p className="text-gray-600 mt-2">Track your progress and stay motivated</p>
      </div>

      {/* Streak Banner */}
      <div className="card bg-gradient-to-r from-orange-500 to-red-500 text-white mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Flame className="w-6 h-6" />
              <h2 className="text-xl font-bold">Current Streak</h2>
            </div>
            <p className="text-4xl font-bold">{streakInfo?.current_streak || 0} days</p>
            <p className="text-orange-100 mt-2">{streakInfo?.message || 'Keep learning!'}</p>
          </div>
          <div className="hidden md:block">
            <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Flame className="w-16 h-16" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cards</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.total_cards || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Due Today</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.due_today || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Reviews</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.total_reviews || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-accent-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Retention Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.avg_retention || 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Card Distribution */}
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Card Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={cardDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {cardDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            {cardDistribution.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="truncate">{entry.name}</span>
                <span className="font-semibold text-gray-900 ml-auto">
                  {totalDistribution ? `${Math.round((entry.value / totalDistribution) * 100)}%` : '0%'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Last 30 Days Activity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={recentActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="axisDate" interval={0} tick={{ fontSize: 10 }} height={45} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="reviews" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Heatmap */}
      <div className="card">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Learning Heatmap (Last Year)</h3>
        {selectedDay && (
          <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-sm">
            <Calendar className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-green-700">
              {new Date(`${selectedDay.date}T00:00:00`).toLocaleDateString('vi-VN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="text-green-600">— {selectedDay.count} reviews</span>
            <button onClick={() => setSelectedDay(null)} className="ml-1 text-green-400 hover:text-green-600">✕</button>
          </div>
        )}
        <div className="overflow-x-auto">
          <div className="flex space-x-1 min-w-max">
            <div
              className="grid gap-1 min-w-max"
              style={{
                gridTemplateRows: 'repeat(7, 12px)',
                gridAutoFlow: 'column',
                gridAutoColumns: '12px'
              }}
            >
            {heatmapCells.map((day, index) => {
              if (!day) {
                return <div key={`padding-${index}`} className="w-3 h-3" />;
              }

              const intensity = day.count === 0 ? 0 :
                               day.count < 5 ? 1 :
                               day.count < 15 ? 2 :
                               day.count < 30 ? 3 : 4;

              const colors = [
                'bg-gray-100',
                'bg-green-200',
                'bg-green-400',
                'bg-green-600',
                'bg-green-800'
              ];

              return (
                <div
                  key={index}
                  className={`w-3 h-3 ${colors[intensity]} rounded-sm cursor-pointer hover:ring-2 hover:ring-green-400 hover:ring-offset-1 transition-all`}
                  title={`${day.date}: ${day.count} reviews`}
                  onClick={() => setSelectedDay(selectedDay?.date === day.date ? null : day)}
                />
              );
            })}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-2 mt-4 text-sm text-gray-600">
          <span>Less</span>
          <div className="w-3 h-3 bg-gray-100 rounded-sm" />
          <div className="w-3 h-3 bg-green-200 rounded-sm" />
          <div className="w-3 h-3 bg-green-400 rounded-sm" />
          <div className="w-3 h-3 bg-green-600 rounded-sm" />
          <div className="w-3 h-3 bg-green-800 rounded-sm" />
          <span>More</span>
        </div>
      </div>

      {/* Achievements */}
      <div className="card mt-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Achievements</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`text-center p-4 rounded-lg ${stats?.total_cards >= 100 ? 'bg-yellow-100' : 'bg-gray-100 opacity-50'}`}>
            <Award className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
            <p className="text-sm font-medium">100 Cards</p>
          </div>
          <div className={`text-center p-4 rounded-lg ${stats?.streak_days >= 7 ? 'bg-orange-100' : 'bg-gray-100 opacity-50'}`}>
            <Flame className="w-8 h-8 mx-auto mb-2 text-orange-600" />
            <p className="text-sm font-medium">7 Day Streak</p>
          </div>
          <div className={`text-center p-4 rounded-lg ${stats?.total_reviews >= 500 ? 'bg-blue-100' : 'bg-gray-100 opacity-50'}`}>
            <Brain className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <p className="text-sm font-medium">500 Reviews</p>
          </div>
          <div className={`text-center p-4 rounded-lg ${stats?.mastered_cards >= 50 ? 'bg-green-100' : 'bg-gray-100 opacity-50'}`}>
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="text-sm font-medium">50 Mastered</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats;
